import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Phone, Menu, X } from 'lucide-react';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/#services' },
    { name: 'Service Areas', path: '/service-areas' },
    { name: 'Parts', path: '/parts' },
    { name: 'Blog', path: '/blog' },
    { name: 'Book Online', path: '/book' },
  ];

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'nav-glass shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_home-fix-pros-3/artifacts/7ckw9a8p_LOGO%20BBM.png" 
              alt="BBM Appliances Logo"
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                className={`font-medium transition-colors hover:text-primary ${
                  location.pathname === link.path ? 'text-primary' : 'text-foreground'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:+18556056034"
              data-testid="phone-cta"
              className="flex items-center gap-2 text-primary font-semibold hover:opacity-80 transition-opacity"
            >
              <Phone className="w-5 h-5" />
              <span>(855) 605-6034</span>
            </a>
            <Link to="/book">
              <Button
                data-testid="book-now-btn"
                className="bg-accent hover:bg-accent/90 text-white font-semibold px-6 rounded-full btn-shine"
              >
                Book Now
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 rounded-lg hover:bg-secondary"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div data-testid="mobile-menu" className="lg:hidden mobile-menu border-t">
          <div className="px-4 py-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                data-testid={`mobile-nav-${link.name.toLowerCase().replace(' ', '-')}`}
                className="block py-3 font-medium text-foreground hover:text-primary transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            <div className="pt-4 space-y-3">
              <a
                href="tel:+18556056034"
                data-testid="mobile-phone-cta"
                className="flex items-center justify-center gap-2 py-3 text-primary font-semibold"
              >
                <Phone className="w-5 h-5" />
                <span>(855) 605-6034</span>
              </a>
              <Link to="/book" onClick={() => setIsMobileMenuOpen(false)}>
                <Button
                  data-testid="mobile-book-btn"
                  className="w-full bg-accent hover:bg-accent/90 text-white font-semibold rounded-full"
                >
                  Book Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
